import { eventTracker } from 'app/modules/Segment';
import {
  TProperties,
  TContext,
  GroupIdEnum,
  OwnerEnum,
  FeatureEnum,
} from 'app/modules/Segment/types';
import { snakeCaseToTitleCase } from 'app/modules/Segment/utils';

export const EVENTS = {
  PLAN_UPGRADED: 'plan_upgraded',
  PLAN_SELECTED: 'plan_selected',
  UPGRADE_STARTED: 'upgrade_started',
  UPGRADE_PAYMENT_SUCCESS: 'upgrade_payment_success',
  UPGRADE_PAYMENT_FAILED: 'upgrade_payment_failed',
  CREDIT_CARD_ADDED: 'credit_card_added',
  UPGRADE_CARD_TOKENIZATION_SUCCESS: 'upgrade_card_tokenization_success',
  UPGRADE_CARD_TOKENIZATION_ERROR: 'upgrade_card_tokenization_error',
  PLATFORM_PLAN_CANCEL: 'platform_plan_cancel',
  PLATFORM_PLAN_CANCEL_CONFIRMED: 'platform_plan_cancel_confirmed',
  PLATFORM_PLAN_CANCEL_SUCCESS: 'platform_plan_cancel_success',
  PLATFORM_PLAN_CANCEL_ERROR: 'platform_plan_cancel_error',
  PAYMENT_LINK_VALIDATED: 'payment_link_validated',
  PAYMENT_LINK_STARTED: 'payment_link_started',
  PAYMENT_LINK_ALREADY_PAID: 'payment_link_already_paid',
  PAYMENT_LINK_CARD_TOKENIZATION_SUCCESS:
    'payment_link_card_tokenization_success',
  PAYMENT_LINK_CARD_TOKENIZATION_ERROR: 'payment_link_card_tokenization_error',
  PAYMENT_LINK_SUBMITTED_BUTTON_CLICKED:
    'payment_link_submitted_button_clicked',
  PAYMENT_LINK_POLLING_STARTED: 'payment_link_polling_started',
  PAYMENT_LINK_PAYMENT_SUCCESS: 'payment_link_payment_success',
  PAYMENT_LINK_PAYMENT_FAILED: 'payment_link_payment_failed',
  PAYMENT_LINK_SUBMISSION_FAILED: 'payment_link_submission_failed',
  PAYMENT_LINK_EXPIRED: 'payment_link_expired',
  PAYMENT_LINK_POLLING_TIMEOUT: 'payment_link_polling_timeout',
  LOGGED_CHECKOUT_CARD_TOKENIZATION_SUCCESS:
    'logged_checkout_card_tokenization_success',
  LOGGED_CHECKOUT_CARD_TOKENIZATION_ERROR:
    'logged_checkout_card_tokenization_error',
  LOGGED_CHECKOUT_PAYMENT_SUCCESS: 'logged_checkout_payment_success',
  LOGGED_CHECKOUT_PAYMENT_FAILED: 'logged_checkout_payment_failed',
  LOGGED_CHECKOUT_PIX_PAYMENT_PAID: 'logged_checkout_pix_payment_paid',
  LOGGED_CHECKOUT_TALK_TO_SUPPORT_CLICKED:
    'logged_checkout_talk_to_support_clicked',
  LOGGED_CHECKOUT_PAYMENT_METHOD_CHANGED:
    'logged_checkout_payment_method_changed',
  LOGGED_CHECKOUT_PAYMENT_BOLETO_CLICKED:
    'logged_checkout_payment_boleto_clicked',
  LOGGED_CHECKOUT_PAY_INVOICE_BUTTON_CLICKED:
    'logged_checkout_pay_invoice_button_clicked',
  SAVE_CARD_API_FAILED: 'save_card_api_failed',
  SAVE_CARD_REQUEST_FAILED: 'save_card_request_failed',
} as const;

const GENERAL_LIBRARY_NAME = 'general-library';
const UPGRADE_EXPANSION_CATEGORY = 'upgrade_expansion';
const OTHER_CATEGORY = 'other';
const ERROR_TECHNICAL_CATEGORY = 'error_technical';

const planUpgradedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'plan-upgraded-component',
  },
};

const planSelectedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'plan-selected-component',
  },
};

const upgradeStartedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'upgrade-started-component',
  },
};

const upgradePaymentSuccessContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'payment-success-component',
  },
};

const upgradePaymentFailedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'payment-failed-component',
  },
};

const PAYMENT_FAILURE_CATEGORY = 'payment_failure';

const ONBOARDING_PAYMENT_CATEGORY = 'onboarding_payment';

const paymentLinkValidatedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'link-validated-component',
  },
};

const paymentLinkStartedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'link-started-component',
  },
};

const paymentLinkAlreadyPaidContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'already-paid-component',
  },
};

const paymentLinkCardTokenizationSuccessContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'card-component',
  },
};

const paymentLinkSubmittedButtonClickedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'button-component',
  },
};

const paymentLinkPollingStartedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'polling-started-component',
  },
};

const paymentLinkPaymentFailedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'payment-failed-component',
  },
};

const paymentLinkPaymentSuccessContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'payment-success-component',
  },
};

const paymentLinkExpiredContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'link-expired-component',
  },
};

const paymentLinkSubmissionFailedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'submission-failed-component',
  },
};

const creditCardAddedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'card-component',
  },
};

const loggedCheckoutPixPaymentPaidContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'payment-paid-component',
  },
};

const loggedCheckoutPaymentMethodChangedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'method-change-component',
  },
};

const loggedCheckoutBoletoClickedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'button-component',
  },
};

const loggedCheckoutPayInvoiceButtonClickedContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'button-component',
  },
};

const CANCEL_INTENT_CATEGORY = 'cancel_intent';

/**
 * Subset que alimenta o filtro de segmentação do HubSpot (`filtro_hubspot: 1`).
 * Os 4 eventos platform_plan_cancel_* (fluxo antigo de cancelamento) alimentam
 * o mesmo funil de segmentação que os cancel_flow_* do fluxo novo.
 */
const HUBSPOT_FILTER_EVENTS = new Set<string>([
  EVENTS.PLATFORM_PLAN_CANCEL,
  EVENTS.PLATFORM_PLAN_CANCEL_CONFIRMED,
  EVENTS.PLATFORM_PLAN_CANCEL_SUCCESS,
  EVENTS.PLATFORM_PLAN_CANCEL_ERROR,
]);

const platformPlanCancelContext: TContext = {
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: 'form-component',
  },
};

export function trackPlatformPlanCancel(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLATFORM_PLAN_CANCEL;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    ...(HUBSPOT_FILTER_EVENTS.has(event) ? { filtro_hubspot: 1 } : {}),
    feature: FeatureEnum.SUBSCRIPTION_CANCEL,
    category: CANCEL_INTENT_CATEGORY,
    event_meaning:
      '[Cancelamento assinatura na plataforma] Clique no botão cancelar assinatura em meu perfil>assinaturas>cancelarplano na plataforma. Válido para V4 e V5.',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: platformPlanCancelContext,
  });
}

export function trackPlatformPlanCancelConfirmed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLATFORM_PLAN_CANCEL_CONFIRMED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    ...(HUBSPOT_FILTER_EVENTS.has(event) ? { filtro_hubspot: 1 } : {}),
    feature: FeatureEnum.SUBSCRIPTION_CANCEL,
    category: CANCEL_INTENT_CATEGORY,
    event_meaning:
      '[Cancelamento assinatura na plataforma] Clique no botão Cancelar conta! no modal de cancelamento de assinatura na plataforma. Válido para V4 e V5.',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: platformPlanCancelContext,
  });
}

export function trackPlatformPlanCancelSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLATFORM_PLAN_CANCEL_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    ...(HUBSPOT_FILTER_EVENTS.has(event) ? { filtro_hubspot: 1 } : {}),
    feature: FeatureEnum.SUBSCRIPTION_CANCEL,
    category: CANCEL_INTENT_CATEGORY,
    event_meaning:
      '[Cancelamento assinatura na plataforma] Retorno da api com sucesso em realizar o cancelamento após clique em Cancelar conta! no modal de cancelamento de assinatura na plataforma',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: platformPlanCancelContext,
  });
}

export function trackPlatformPlanCancelError(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLATFORM_PLAN_CANCEL_ERROR;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    ...(HUBSPOT_FILTER_EVENTS.has(event) ? { filtro_hubspot: 1 } : {}),
    feature: FeatureEnum.SUBSCRIPTION_CANCEL,
    category: CANCEL_INTENT_CATEGORY,
    event_meaning:
      '[Cancelamento assinatura na plataforma] Retorno da api com erro em realizar o cancelamento após clique em Cancelar conta! no modal de cancelamento de assinatura na plataforma',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: platformPlanCancelContext,
  });
}

export function trackUpgradeStarted(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.UPGRADE_STARTED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: UPGRADE_EXPANSION_CATEGORY,
    event_meaning:
      'Mesmo evento que limit_reach_upgrade_button_clicked (este último é mais atualizado e possui maior histórico de dados)',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: upgradeStartedContext,
  });
}

export function trackPlanUpgraded(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLAN_UPGRADED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    filtro_hubspot: 1,
    category: UPGRADE_EXPANSION_CATEGORY,
    event_meaning:
      '[Troca de plano na plataforma] Clique no botão assinar agora para realizar pagamento da troca de plano (este evento não mostra sucesso/erro do pagamento em si)',
    relevant_user_activity: true,
    event_text: snakeCaseToTitleCase(event),
  };

  eventTracker.track({
    event,
    properties,
    context: planUpgradedContext,
  });
}

export function trackPlanSelected(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PLAN_SELECTED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Troca de plano na plataforma] Clique ao selecionar um dos planos no modal de Upsell na plataforma. Este evento não acontece ao selecionar um plano no Checkout ou selecionar um plano em Meu Perfil.',
    relevant_user_activity: false,
    event_text: 'Plan Selected',
  };

  eventTracker.track({
    event,
    properties,
    context: planSelectedContext,
  });
}

export function trackUpgradeCardTokenizationSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.UPGRADE_CARD_TOKENIZATION_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: UPGRADE_EXPANSION_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com sucesso na tokenização de cartão no fluxo de troca de plano na plataforma',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackUpgradeCardTokenizationError(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.UPGRADE_CARD_TOKENIZATION_ERROR;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ERROR_TECHNICAL_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com erro na tokenização de cartão no fluxo de troca de plano na plataforma',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackUpgradePaymentSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.UPGRADE_PAYMENT_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: UPGRADE_EXPANSION_CATEGORY,
    event_meaning:
      '[Troca de plano na plataforma] Sucesso no pagamento da troca de plano na plataforma. Válido para todos os contextos de troca de plano na plataforma como perfil, jusfinder, calculadoras.',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: upgradePaymentSuccessContext,
  });
}

export function trackUpgradePaymentFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.UPGRADE_PAYMENT_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com erro na tokenização de cartão no fluxo de troca de plano na plataforma',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: upgradePaymentFailedContext,
  });
}

export function trackCreditCardAdded(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.CREDIT_CARD_ADDED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: UPGRADE_EXPANSION_CATEGORY,
    event_meaning:
      '[Adição de cartão na plataforma] Clique em adicionar cartão após preenchimento do modal de dados do cartão em meu perfil>cartões>adicionar cartão',
    relevant_user_activity: true,
    event_text: snakeCaseToTitleCase(event),
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackLoggedCheckoutCardTokenizationSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_CARD_TOKENIZATION_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com sucesso na tokenização de cartão de crédito, após o clique no botão Concluir pagamento no checkout logado.',
    relevant_user_activity: false,
    event_text: 'Checkout Card Tokenization Success',
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackLoggedCheckoutCardTokenizationError(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_CARD_TOKENIZATION_ERROR;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ERROR_TECHNICAL_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com erro na tokenização de cartão de crédito, após o clique no botão Concluir pagamento no checkout logado.',
    relevant_user_activity: false,
    event_text: 'Checkout Card Tokenization Error',
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackLoggedCheckoutPaymentSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PAYMENT_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Sucesso ao concluir pagamento no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Payment Success',
  };

  eventTracker.track({
    event,
    properties,
    context: upgradePaymentSuccessContext,
  });
}

export function trackLoggedCheckoutPaymentFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PAYMENT_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Erro ao concluir pagamento no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Payment Failed',
  };

  eventTracker.track({
    event,
    properties,
    context: upgradePaymentFailedContext,
  });
}

export function trackLoggedCheckoutPixPaymentPaid(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PIX_PAYMENT_PAID;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Sucesso na geração do Pix, após clique no botão pagar com Pix no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Pix Payment Paid',
  };

  eventTracker.track({
    event,
    properties,
    context: loggedCheckoutPixPaymentPaidContext,
  });
}

export function trackLoggedCheckoutTalkToSupportClicked(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_TALK_TO_SUPPORT_CLICKED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Clique no botão Falar com suporteno checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Talk To Support Clicked',
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkSubmittedButtonClickedContext,
  });
}

export function trackLoggedCheckoutPaymentMethodChanged(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PAYMENT_METHOD_CHANGED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Clique em alterar de método de pagamento para Pix no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Payment Method Change',
  };

  eventTracker.track({
    event,
    properties,
    context: loggedCheckoutPaymentMethodChangedContext,
  });
}

export function trackLoggedCheckoutAccessBoletoUrl(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PAYMENT_BOLETO_CLICKED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Clique para acessar URL do boleto no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Payment Boleto Clicked',
  };

  eventTracker.track({
    event,
    properties,
    context: loggedCheckoutBoletoClickedContext,
  });
}

export function trackLoggedCheckoutPayInvoiceButtonClicked(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.LOGGED_CHECKOUT_PAY_INVOICE_BUTTON_CLICKED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: OTHER_CATEGORY,
    event_meaning:
      '[Checkout - Logado] Clique no botão de pagar fatura / reativar plano no checkout logado',
    relevant_user_activity: false,
    event_text: 'Checkout Pay Invoice Button Clicked',
  };

  eventTracker.track({
    event,
    properties,
    context: loggedCheckoutPayInvoiceButtonClickedContext,
  });
}

export function trackPaymentLinkValidated(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_VALIDATED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning:
      '[Link de pagamento] Resposta do navegador de que o link de pagamento abriu válido',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkValidatedContext,
  });
}

export function trackPaymentLinkStarted(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_STARTED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning: '[Link de pagamento] Link de pagamento foi aberto/iniciado.',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkStartedContext,
  });
}

export function trackPaymentLinkAlreadyPaid(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_ALREADY_PAID;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning:
      '[Link de pagamento] Ao abrir o link de pagamento, a mensagem exibida é de link já pago',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkAlreadyPaidContext,
  });
}

export function trackPaymentLinkCardTokenizationSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_CARD_TOKENIZATION_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com sucesso na tokenização de cartão no fluxo de link de pagamento. Somente V5',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkCardTokenizationSuccessContext,
  });
}

export function trackPaymentLinkCardTokenizationError(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_CARD_TOKENIZATION_ERROR;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Tokenização de cartão] Retorno da api com erro na tokenização de cartão no fluxo de link de pagamento. Somente V5',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkCardTokenizationSuccessContext,
  });
}

export function trackPaymentLinkSubmittedButtonClicked(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_SUBMITTED_BUTTON_CLICKED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning:
      '[Link de pagamento] Clicou em confirmar e pagar dentro do link de pagamento',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkSubmittedButtonClickedContext,
  });
}

export function trackPaymentLinkPollingStarted(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_POLLING_STARTED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning: '[Link de pagamento] Pooling iniciado',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkPollingStartedContext,
  });
}

export function trackPaymentLinkPaymentSuccess(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_PAYMENT_SUCCESS;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ONBOARDING_PAYMENT_CATEGORY,
    event_meaning:
      '[Link de pagamento] Sucesso ao concluir o pagamento via link de pagamento',
    relevant_user_activity: true,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkPaymentSuccessContext,
  });
}

export function trackPaymentLinkPaymentFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_PAYMENT_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Link de pagamento] Erro ao concluir o pagamento via link de pagamento',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkPaymentFailedContext,
  });
}

export function trackPaymentLinkExpired(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_EXPIRED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Link de pagamento] Ao abrir o link de pagamento, a mensagem exibida é de link expirado (isso ocorre quando o suporte força a expiração do link, via admin, ou após o prazo de expiração automático do link de pagamento)',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkExpiredContext,
  });
}

export function trackPaymentLinkSubmissionFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_SUBMISSION_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Link de pagamento] Falha após clique em confirmar e pagar, que ocorre antes do gateway de pagamento',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkSubmissionFailedContext,
  });
}

export function trackPaymentLinkPollingTimeout(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.PAYMENT_LINK_POLLING_TIMEOUT;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.JUSFY_PAY,
    category: PAYMENT_FAILURE_CATEGORY,
    event_meaning:
      '[Link de pagamento] Timeout no polling da assinatura após confirmação do pagamento',
    relevant_user_activity: false,
    event_text: event,
  };

  eventTracker.track({
    event,
    properties,
    context: paymentLinkPollingStartedContext,
  });
}

export function trackSaveCardApiFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.SAVE_CARD_API_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    category: ERROR_TECHNICAL_CATEGORY,
    event_meaning: '[Troca de plano plataforma - jusprocessos]',
    relevant_user_activity: false,
    event_text: 'Save Card API Failed',
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}

export function trackSaveCardRequestFailed(
  additionalProperties?: Record<string, unknown>,
): void {
  const event = EVENTS.SAVE_CARD_REQUEST_FAILED;

  const properties: TProperties = {
    ...(additionalProperties ?? {}),
    owner: OwnerEnum.PAGAMENTOS,
    feature: FeatureEnum.CHECKOUT,
    category: ERROR_TECHNICAL_CATEGORY,
    event_meaning:
      '[Troca de plano plataforma] Erro - O front não consegue enviar a requisição ou o backend não consegue acessar o gateway para obter resposta. Evento acontece geralmente quando o backend-api está fora, possivelmente em algum momento de deploy.',
    relevant_user_activity: false,
    event_text: 'Save Card Request Failed',
  };

  eventTracker.track({
    event,
    properties,
    context: creditCardAddedContext,
  });
}
